import { colorToCss } from "@omnia/shared/color/color";
import { CropStyleFillPattern } from "@omnia/shared/generated/enums";
import { ColorPickerButton } from "@omnia/shared/react/ColorPicker";
import { CropLabel } from "@omnia/shared/react/CropLabel";
import { classes } from "@omnia/shared/react/react";
import { SelectNumber } from "@omnia/shared/react/Select";
import { RGBColor } from "@omnia/shared/types";
import { compareStyles } from "../changes";
import { BasicStyle, PATTERN_OPTIONS } from "../types";
import { IconButton } from "./IconButton";
import { StylePreview } from "./StylePreview";

export interface StyleRowProps {
    className?: string;
    /**
     * The current style selected in this row.
     */
    selectedStyle: BasicStyle;
    /**
     * The initial selected style for this row.
     */
    initialStyle: BasicStyle;
    /**
     * The effective style used to display this row.
     */
    effectiveStyle: BasicStyle;
    /**
     * The label for this row.
     */
    label?: string;
    /**
     * The type label for this row.
     */
    typeLabel?: string;
    /**
     * Event handler called whenever the style is changed (via color or pattern)
     * @param value The new style value.
     */
    onChange?: React.Dispatch<React.SetStateAction<BasicStyle>>;
    /**
     * Event handler called whenever the row is toggled expanded/collapsed.
     */
    onToggle?: () => void;
    /**
     * Whether the row is expanded, or collapsed.
     */
    expanded?: boolean;
}

export const StyleRow = ({
    className,
    selectedStyle,
    initialStyle,
    effectiveStyle = selectedStyle,
    label,
    onChange,
    typeLabel,
    children,
    onToggle,
    expanded,
}: React.PropsWithChildren<StyleRowProps>) => {
    const onChangeColor = React.useCallback(
        (value: RGBColor) => {
            onChange((prev) => ({
                ...prev,
                color: value,
                pattern: value ? prev.pattern ?? CropStyleFillPattern.None : null,
            }));
        },
        [onChange]
    );

    const onChangePattern = React.useCallback(
        (value: CropStyleFillPattern) => {
            onChange((prev) => ({
                ...prev,
                pattern: value,
            }));
        },
        [onChange]
    );

    const onResetToInitial = React.useCallback(() => {
        onChange({ ...initialStyle });
    }, [onChange, initialStyle]);

    const onDeleteSelected = React.useCallback(() => {
        onChange({
            color: null,
            pattern: null,
        });
    }, [onChange]);

    const hasStyle = Boolean(selectedStyle && selectedStyle.color);

    const hasChanged = !compareStyles(selectedStyle, initialStyle);

    const isExpandable = Boolean(expanded !== undefined && onToggle);

    const cn = (suffix: string) => `style-row--${suffix}`;

    return (
        <>
            <tr className={classes("style-row", className)}>
                <td className={cn("toggle")}>
                    {isExpandable ? (
                        <button type="button" className="btn btn-link btn-icon-toggle" onClick={onToggle}>
                            <i className={`fas ${expanded ? "fa-chevron-up" : "fa-chevron-down"}`} />
                        </button>
                    ) : null}
                </td>
                <td className={cn("label")}>
                    <CropLabel color={colorToCss(effectiveStyle.color)}>{label}</CropLabel>
                </td>
                <td className={cn("type")}>{typeLabel}</td>
                <td className={cn("color")}>
                    <ColorPickerButton color={selectedStyle.color} setColor={onChangeColor} closeWhenSelected={false} />
                </td>
                <td className={cn("pattern")}>
                    {hasStyle && (
                        <SelectNumber
                            setValue={onChangePattern}
                            value={selectedStyle.pattern}
                            options={PATTERN_OPTIONS}
                            disabled={!hasStyle}
                        />
                    )}
                </td>
                <td className={cn("preview")}>
                    <StylePreview color={effectiveStyle.color} pattern={effectiveStyle.pattern} onPaste={onChange} />
                </td>
                <td className={cn("reset")}>
                    {hasChanged ? (
                        <IconButton onClick={onResetToInitial} icon="fas fa-undo-alt" title="Revert unsaved changes" />
                    ) : hasStyle ? (
                        <IconButton onClick={onDeleteSelected} icon="fas fa-trash" title="Clear custom style" />
                    ) : null}
                </td>
            </tr>
            {isExpandable && expanded ? children : null}
        </>
    );
};
